import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

type PaginationAccent = "blue" | "green" | "purple";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  accent?: PaginationAccent;
  className?: string;
}

const accentClasses: Record<
  PaginationAccent,
  {
    button: string;
    badge: string;
  }
> = {
  blue: {
    button:
      "border-blue-200 text-blue-700 hover:bg-blue-50 disabled:hover:bg-white",
    badge: "bg-blue-50 border-blue-200 text-blue-700",
  },
  green: {
    button:
      "border-green-200 text-green-700 hover:bg-green-50 disabled:hover:bg-white",
    badge: "bg-green-50 border-green-200 text-green-700",
  },
  purple: {
    button:
      "border-purple-200 text-purple-700 hover:bg-purple-50 disabled:hover:bg-white",
    badge: "bg-purple-50 border-purple-200 text-purple-700",
  },
};

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  accent = "blue",
  className = "",
}) => {
  const { t } = useTranslation("common");

  if (totalItems <= itemsPerPage || totalPages <= 1) {
    return null;
  }

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);
  const styles = accentClasses[accent];

  return (
    <div
      className={`mt-4 flex items-center justify-between gap-3 flex-wrap ${className}`.trim()}
    >
      <div className="text-xs text-gray-500">
        {start}-{end} / {totalItems}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles.button}`}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {t("previous")}
        </button>

        <span
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${styles.badge}`}
        >
          {currentPage} / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles.button}`}
        >
          {t("next")}
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
